<template>
  <section id="News">
    <div class="container">
      <h1 class="section-title">Press Coverage</h1>
      <p class="intro-text">
        See what the media is saying about Stardust's mission to protect privacy on the web.
      </p>

      <!-- Article Cards Grid -->
      <div class="article-grid">
        <a
          v-for="article in articles"
          :key="article.id"
          :href="article.url"
          target="_blank"
          rel="noopener noreferrer"
          class="article-card"
          :aria-label="'Read article: ' + article.title">

          <!-- Article Image -->
          <div class="article-image-wrapper">
            <img
              class="article-image"
              :src="article.img"
              :alt="'Thumbnail for article: ' + article.title"
              loading="lazy"
            />
            <div class="image-overlay"></div>
          </div>

          <!-- Article Content -->
          <div class="article-content">
            <h3 class="article-title">{{ article.title }}</h3>

            <div class="article-meta">
              <span class="meta-item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" class="meta-icon">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <circle cx="12" cy="7" r="4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                {{ article.author }}
              </span>
              <span class="meta-divider">•</span>
              <span class="meta-item">{{ article.publication }}</span>
            </div>

            <!-- Article Details -->
            <div class="article-details">
              <span class="detail-badge">
                {{ article.lang[1] }}
              </span>
              <span class="detail-badge" :class="article.paywall ? 'paywall' : 'free'">
                {{ article.paywall ? "Paywall" : "Free" }}
              </span>
            </div>

            <!-- Publication Date -->
            <div class="article-date">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" class="date-icon">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              {{ article.releaseMonth }} {{ article.releaseDay }}, {{ article.releaseYear }}
            </div>
          </div>
        </a>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "NewsFeatures",
  data() {
    return {
      articles: [
        {
          id: 1,
          title: "Isikuandmete Netflix leevendab 'küpsiste' hirmu",
          author: "Johanna Adojaan",
          publication: "foundME",
          releaseDay: "22",
          releaseMonth: "September",
          releaseYear: "2021",
          url: "https://www.foundme.io/uudised/2021/09/20/isikuandmete-netflix-leevendab-kupsiste-hirmu",
          img: "https://static-img.aripaev.ee/?type=preview&uuid=053c72d2-67cd-5dcd-9965-1770857ba05a&width=2000&q=85",
          lang: ["en", "🇪🇪"],
          paywall: true,
        },
        {
          id: 2,
          title: "A new era of data privacy",
          author: "Til Jordan (with GPT-3)",
          publication: "Medium",
          releaseDay: "30",
          releaseMonth: "July",
          releaseYear: "2021",
          url: "https://medium.com/@tiljrd/a-new-era-of-data-privacy-d45ae7bd1dcb",
          img: "https://miro.medium.com/max/1400/1*Nfuf_XFB6whLYOpuZA9t2w.jpeg",
          lang: ["en", "🇺🇸🇬🇧"],
          paywall: false,
        },
        {
          id: 3,
          title: "Stardust launches time saving 'Cookie Cutter'",
          author: "Alex Dunlop",
          publication: "Future Worlds",
          releaseDay: "31",
          releaseMonth: "March",
          releaseYear: "2021",
          url: "https://futureworlds.com/stardust-launches-time-saving-cookie-cutter/",
          img: "https://futureworlds.com/wp-content/uploads/2021/03/16-9v1-1024x576.jpg",
          lang: ["en", "🇺🇸🇬🇧"],
          paywall: false,
        },
      ],
    };
  },
};
</script>

<style scoped>
section {
  background-color: var(--color-charcoal-mist-70);
  backdrop-filter: var(--blur-glassmorphism);
  filter: var(--shadow-card);
  padding: var(--spacing-3xl) var(--container-padding);
  margin: 0;
}

.container {
  max-width: var(--container-max-width);
  margin: 0 auto;
}

.section-title {
  text-align: center;
  color: var(--color-pure-white);
  font-size: clamp(32px, 4.5vw, 42px);
  font-weight: var(--font-weight-bold);
  margin: 0 0 var(--spacing-md) 0;
  font-family: var(--font-primary);
}

.intro-text {
  max-width: 700px;
  margin: 0 auto var(--spacing-3xl);
  text-align: center;
  font-size: clamp(15px, 1.8vw, 17px);
  line-height: 1.7;
  color: var(--color-soft-white);
  font-family: var(--font-secondary);
}

/* Article Grid */
.article-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: var(--spacing-xl);
  width: 100%;
}

/* Article Card */
.article-card {
  background: var(--color-charcoal-mist-50);
  border: 2px solid transparent;
  border-radius: var(--radius-large);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: var(--transition-premium);
  text-decoration: none;
  cursor: pointer;
  position: relative;
}

.article-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, var(--color-gold-glow-15) 0%, transparent 100%);
  opacity: 0;
  transition: var(--transition-standard);
  pointer-events: none;
  z-index: 1;
}

.article-card:hover {
  transform: translateY(-8px);
  border-color: var(--color-gold-glow-30);
  background: var(--color-charcoal-mist-70);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.4);
}

.article-card:hover::before {
  opacity: 1;
}

.article-card:focus-visible {
  outline: 3px solid var(--color-stardust-gold);
  outline-offset: 3px;
}

/* Article Image */
.article-image-wrapper {
  position: relative;
  width: 100%;
  height: 220px;
  overflow: hidden;
  background: var(--color-deep-ocean);
}

.article-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: var(--transition-premium);
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(180deg, transparent 0%, rgba(24, 34, 38, 0.3) 100%);
  opacity: 0;
  transition: var(--transition-standard);
}

.article-card:hover .article-image {
  transform: scale(1.05);
}

.article-card:hover .image-overlay {
  opacity: 1;
}

/* Article Content */
.article-content {
  padding: var(--spacing-lg);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  position: relative;
  z-index: 2;
  flex: 1;
}

.article-title {
  font-size: clamp(18px, 2.2vw, 22px);
  font-weight: var(--font-weight-semibold);
  color: var(--color-pure-white);
  margin: 0 0 var(--spacing-sm) 0;
  font-family: var(--font-primary);
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  transition: var(--transition-quick);
}

.article-card:hover .article-title {
  color: var(--color-stardust-gold);
}

/* Article Meta */
.article-meta {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-size: 14px;
  color: var(--color-muted-white-70);
  font-family: var(--font-secondary);
  flex-wrap: wrap;
}

.meta-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.meta-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: var(--color-stardust-gold);
}

.meta-divider {
  color: var(--color-border-subtle-30);
}

/* Article Details */
.article-details {
  display: flex;
  gap: var(--spacing-xs);
  flex-wrap: wrap;
  margin-top: auto;
  padding-top: var(--spacing-sm);
}

.detail-badge {
  padding: 4px 12px;
  border-radius: var(--radius-pill);
  font-size: 12px;
  font-weight: var(--font-weight-medium);
  font-family: var(--font-secondary);
  background: var(--color-charcoal-mist-70);
  border: 1px solid var(--color-border-subtle);
  color: var(--color-pure-white);
  white-space: nowrap;
}

.detail-badge.free {
  background: var(--color-gold-glow-15);
  border-color: var(--color-gold-glow-30);
  color: var(--color-stardust-gold);
}

.detail-badge.paywall {
  background: rgba(255, 255, 255, 0.05);
  border-color: var(--color-border-subtle);
  color: var(--color-muted-white-70);
}

/* Article Date */
.article-date {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--color-muted-white);
  font-family: var(--font-secondary);
  padding-top: var(--spacing-xs);
  border-top: 1px solid var(--color-border-subtle);
}

.date-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  color: var(--color-muted-white);
}

/* Responsive Design */
@media (max-width: 768px) {
  section {
    padding: var(--spacing-2xl) var(--container-padding-tablet);
  }

  .article-grid {
    grid-template-columns: 1fr;
    gap: var(--spacing-lg);
  }

  .article-image-wrapper {
    height: 200px;
  }

  .article-content {
    padding: var(--spacing-md);
  }
}

@media (max-width: 500px) {
  section {
    padding: 30px var(--container-padding-mobile);
  }

  .article-image-wrapper {
    height: 180px;
  }

  .article-content {
    padding: var(--spacing-md);
    gap: var(--spacing-xs);
  }

  .article-meta {
    font-size: 13px;
  }

  .detail-badge {
    font-size: 11px;
    padding: 3px 10px;
  }
}
</style>
